/**
 * SHA-256.
 *
 * `crypto.subtle.digest` è one-shot: vuole tutti i byte in memoria, e non esiste in contesti
 * non sicuri (http:// su LAN, escluso localhost). Per i file grandi (video, anche centinaia di MB)
 * serve un hash INCREMENTALE: qui ce n'è uno in JS puro (`Sha256`, memoria costante ~ 1 KB).
 *
 * `createHasher` sceglie la strada:
 *   - file piccoli (<= SUBTLE_MAX_BYTES, di norma immagini) e subtle disponibile -> si accumulano
 *     i chunk e si usa crypto.subtle (veloce, nativo);
 *   - altrimenti -> Sha256 incrementale. Se un file annunciato piccolo supera la soglia, i chunk già
 *     accumulati vengono travasati nell'hash incrementale.
 * Limite: l'hash JS costa CPU (stimato 50-150 MB/s su PC/Pi 5, molto meno sulle TV): un video da 500 MB
 * su una TV lenta può richiedere decine di secondi in background. Non blocca la riproduzione perché
 * lavora a chunk di rete, tra un await e l'altro.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export class Sha256 {
  private readonly h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly block = new Uint8Array(64);
  private readonly w = new Uint32Array(64);
  private blockLen = 0;
  private total = 0;

  update(data: Uint8Array): this {
    this.total += data.length;
    let i = 0;
    if (this.blockLen > 0) {
      const take = Math.min(64 - this.blockLen, data.length);
      this.block.set(data.subarray(0, take), this.blockLen);
      this.blockLen += take;
      i = take;
      if (this.blockLen === 64) {
        this.compress(this.block, 0);
        this.blockLen = 0;
      }
    }
    for (; i + 64 <= data.length; i += 64) this.compress(data, i);
    if (i < data.length) {
      this.block.set(data.subarray(i), 0);
      this.blockLen = data.length - i;
    }
    return this;
  }

  digestHex(): string {
    const bitsHi = Math.floor((this.total * 8) / 0x1_0000_0000);
    const bitsLo = (this.total * 8) >>> 0;
    const pad = new Uint8Array(((this.blockLen < 56 ? 56 : 120) - this.blockLen) + 8);
    pad[0] = 0x80;
    const dv = new DataView(pad.buffer);
    dv.setUint32(pad.length - 8, bitsHi);
    dv.setUint32(pad.length - 4, bitsLo);
    // `update` aggiorna `total`, ma la lunghezza è già stata catturata sopra
    this.update(pad);
    let out = '';
    for (const v of this.h) out += v.toString(16).padStart(8, '0');
    return out;
  }

  private compress(data: Uint8Array, off: number): void {
    const w = this.w;
    for (let t = 0; t < 16; t++) {
      const j = off + t * 4;
      w[t] = ((data[j] as number) << 24) | ((data[j + 1] as number) << 16) | ((data[j + 2] as number) << 8) | (data[j + 3] as number);
    }
    for (let t = 16; t < 64; t++) {
      const a = w[t - 15] as number;
      const b = w[t - 2] as number;
      const s0 = ((a >>> 7) | (a << 25)) ^ ((a >>> 18) | (a << 14)) ^ (a >>> 3);
      const s1 = ((b >>> 17) | (b << 15)) ^ ((b >>> 19) | (b << 13)) ^ (b >>> 10);
      w[t] = ((w[t - 16] as number) + s0 + (w[t - 7] as number) + s1) | 0;
    }
    const h = this.h;
    let a = h[0] as number, b = h[1] as number, c = h[2] as number, d = h[3] as number;
    let e = h[4] as number, f = h[5] as number, g = h[6] as number, hh = h[7] as number;
    for (let t = 0; t < 64; t++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + (K[t] as number) + (w[t] as number)) | 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      hh = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    h[0] = ((h[0] as number) + a) | 0; h[1] = ((h[1] as number) + b) | 0;
    h[2] = ((h[2] as number) + c) | 0; h[3] = ((h[3] as number) + d) | 0;
    h[4] = ((h[4] as number) + e) | 0; h[5] = ((h[5] as number) + f) | 0;
    h[6] = ((h[6] as number) + g) | 0; h[7] = ((h[7] as number) + hh) | 0;
  }
}

export interface Hasher {
  update(chunk: Uint8Array): void;
  hex(): Promise<string>;
}

export const SUBTLE_MAX_BYTES = 8 * 1024 * 1024;

export function createHasher(sizeHint: number, subtle: SubtleCrypto | null | undefined = globalThis.crypto?.subtle): Hasher {
  const useSubtle = !!subtle && sizeHint > 0 && sizeHint <= SUBTLE_MAX_BYTES;
  if (!useSubtle) {
    const h = new Sha256();
    return { update: (c) => void h.update(c), hex: async () => h.digestHex() };
  }
  let chunks: Uint8Array[] | null = [];
  let held = 0;
  let inc: Sha256 | null = null;
  return {
    update(chunk) {
      if (inc) { inc.update(chunk); return; }
      chunks!.push(chunk);
      held += chunk.length;
      if (held > SUBTLE_MAX_BYTES) { // il server ha mandato più del dichiarato: passa all'hash incrementale
        inc = new Sha256();
        for (const c of chunks!) inc.update(c);
        chunks = null;
      }
    },
    async hex() {
      if (inc) return inc.digestHex();
      const all = new Uint8Array(held);
      let o = 0;
      for (const c of chunks!) { all.set(c, o); o += c.length; }
      const d = new Uint8Array(await subtle!.digest('SHA-256', all));
      return Array.from(d, (b) => b.toString(16).padStart(2, '0')).join('');
    },
  };
}
